import * as React from "react";
import { useField, useFormikContext } from "formik";
import { Box, Button, FormHelperText, IconButton, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface Props {
  name: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

interface Row {
  id: number;
  key: string;
  value: string;
}

const rowsToRecord = (rows: Row[]): Record<string, string> => {
  const record: Record<string, string> = {};
  rows.forEach(row => {
    const trimmedKey = row.key.trim();
    if (trimmedKey) {
      record[trimmedKey] = row.value;
    }
  });
  return record;
};

const recordToRows = (record: Record<string, string> | undefined): Row[] => {
  if (!record) {
    return [];
  }
  return Object.entries(record).map(([key, value], index) => ({ id: index, key, value }));
};

const HeadersField: React.FC<Props> = ({ name, label = "Headers", helperText, disabled }) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField<Record<string, string> | undefined>(name);
  const nextIdRef = React.useRef(0);
  const [rows, setRows] = React.useState<Row[]>(() => {
    const initialRows = recordToRows(field.value);
    nextIdRef.current = initialRows.length;
    return initialRows;
  });

  const updateRows = React.useCallback(
    (newRows: Row[]) => {
      setRows(newRows);
      setFieldValue(name, rowsToRecord(newRows));
    },
    [name, setFieldValue]
  );

  const handleKeyChange = React.useCallback(
    (id: number, value: string) => {
      updateRows(rows.map(row => (row.id === id ? { ...row, key: value } : row)));
    },
    [rows, updateRows]
  );

  const handleValueChange = React.useCallback(
    (id: number, value: string) => {
      updateRows(rows.map(row => (row.id === id ? { ...row, value } : row)));
    },
    [rows, updateRows]
  );

  const handleAddRow = React.useCallback(() => {
    const newRow: Row = { id: nextIdRef.current, key: "", value: "" };
    nextIdRef.current += 1;
    updateRows([...rows, newRow]);
  }, [rows, updateRows]);

  const handleRemoveRow = React.useCallback(
    (id: number) => {
      updateRows(rows.filter(row => row.id !== id));
    },
    [rows, updateRows]
  );

  const hasDuplicateKeys = React.useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      const trimmedKey = row.key.trim();
      if (!trimmedKey) {
        continue;
      }
      if (seen.has(trimmedKey)) {
        return true;
      }
      seen.add(trimmedKey);
    }
    return false;
  }, [rows]);

  const errorText = meta.touched && typeof meta.error === "string" ? meta.error : undefined;
  const duplicateText = hasDuplicateKeys ? "Header names must be unique." : undefined;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {rows.map(row => (
          <Stack key={row.id} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Name"
              variant="standard"
              value={row.key}
              onChange={e => handleKeyChange(row.id, e.target.value)}
              disabled={disabled}
              fullWidth
            />
            <TextField
              size="small"
              label="Value"
              variant="standard"
              value={row.value}
              onChange={e => handleValueChange(row.id, e.target.value)}
              disabled={disabled}
              fullWidth
            />
            <IconButton
              aria-label="Remove header"
              size="small"
              onClick={() => handleRemoveRow(row.id)}
              disabled={disabled}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} disabled={disabled}>
            Add Header
          </Button>
        </Box>
      </Stack>
      {(helperText || errorText || duplicateText) && (
        <FormHelperText error={!!errorText || !!duplicateText}>
          {errorText || duplicateText || helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export { HeadersField };
